// Git Init Tool - Initializes a git repository in Lightning FS
// Extends WorkspaceTool - has access to appType, fs, and git
// Creates .git directory, stages all files, and creates initial commit
// Used to set up version control for browser apps that don't have git initialized

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitInitTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitInitTool';
  }
  /**
   * Initialize a git repository
   * @param {string} [authorName='Revolt User'] - Author name for commits
   * @param {string} [authorEmail='user@revolt.app'] - Author email for commits
   * @param {string} [initialMessage='Initial commit'] - Initial commit message
   * @param {boolean} [stageAll=true] - Whether to stage all files after init
   * @param {boolean} [createInitialCommit=true] - Whether to create initial commit
   * @param {boolean} [force=false] - Force init even if .git exists
   * @returns {Promise<Object>} Result with operation status
   */
  async gitInit(
    authorName = "Revolt User",
    authorEmail = "user@revolt.app",
    initialMessage = "Initial commit",
    stageAll = true,
    createInitialCommit = true,
    force = false
  ) {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitInitForBrowser(
          authorName,
          authorEmail,
          initialMessage,
          stageAll,
          createInitialCommit,
          force
        );
      }

      if (this.appType === "local") {
        return await this.gitInitForLocal(
          authorName,
          authorEmail,
          initialMessage,
          stageAll,
          createInitialCommit,
          force
        );
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitInit`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitinit|error",
          color1: "red",
        },
        "Failed to initialize git repository",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git init for browser apps using Lightning FS and isomorphic-git
   */
  async gitInitForBrowser(
    authorName,
    authorEmail,
    initialMessage,
    stageAll,
    createInitialCommit,
    force
  ) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    // Use the git module directly - it should have init, add, commit functions
    const git = this.git;

    if (typeof git.init !== "function") {
      logger.error(
        {
          tags: "tools|gitinit|error",
          color1: "red",
        },
        "Git init is not a function",
        {
          typeofGit: typeof git,
          keys: typeof git === "object" ? Object.keys(git).slice(0, 10) : [],
          hasInit: typeof git.init,
        }
      );
      return this.returnError(
        {},
        "Git init function not found in provided git module"
      );
    }

    const dir = "/files";

    // Check if .git already exists
    const gitExists = await this.checkGitExists(fs);

    if (gitExists && !force) {
      logger.log(
        {
          tags: "tools|gitinit|info",
          color1: "lime",
        },
        "Git repository already exists, skipping init"
      );
      return this.returnSuccess(
        {
          initialized: false,
          alreadyExists: true,
          staged: false,
          committed: false,
        },
        "Git repository already exists"
      );
    }

    // Initialize git repository
    logger.log(
      {
        tags: "tools|gitinit|info",
        color1: "lime",
      },
      "Initializing git repository"
    );
    await git.init({
      fs: this.fs,
      dir,
      defaultBranch: "main",
    });

    let stagedFiles = [];
    let commitHash = null;

    // Stage all files if requested
    if (stageAll) {
      stagedFiles = await this.stageAllFiles(dir, git, fs);
      logger.log(
        {
          tags: "tools|gitinit|info",
          color1: "lime",
        },
        `Staged ${stagedFiles.length} files`,
        {
          stagedFiles: stagedFiles.slice(0, 10), // Log first 10 for debugging
        }
      );
    }

    // Create initial commit if requested and there are staged files
    if (createInitialCommit) {
      if (stagedFiles.length === 0) {
        logger.warn(
          {
            tags: "tools|gitinit|warn",
            color1: "yellow",
          },
          "Cannot create initial commit - no files staged",
          {
            stageAll,
            stagedCount: stagedFiles.length,
          }
        );
      } else {
        // Use GitCommitTool to create the commit (reuses commit logic)
        if (!this.toolSystem?.gitCommitTool) {
          return this.returnError(
            {},
            "GitCommitTool is not available in the tool system"
          );
        }

        try {
          const commitResult = await this.toolSystem.gitCommitTool.gitCommit(
            initialMessage,
            authorName,
            authorEmail
          );

          if (!commitResult.success) {
            throw new Error(commitResult.message || "Failed to create commit");
          }

          commitHash = commitResult.data.commitHash;
          logger.log(
            {
              tags: "tools|gitinit|info",
              color1: "lime",
            },
            `Created initial commit: ${commitHash}`,
            {
              commitHash,
              message: initialMessage,
              stagedCount: stagedFiles.length,
            }
          );
        } catch (commitError) {
          logger.error(
            {
              tags: "tools|gitinit|error",
              color1: "red",
            },
            "Failed to create initial commit",
            {
              error: commitError.message,
              stack: commitError.stack,
              stagedCount: stagedFiles.length,
            }
          );
          throw commitError;
        }
      }
    }

    return this.returnSuccess(
      {
        initialized: true,
        alreadyExists: false,
        staged: stageAll,
        stagedCount: stagedFiles.length,
        stagedFiles,
        committed: !!commitHash,
        commitHash,
        branch: "main",
      },
      "Git repository initialized successfully"
    );
  }

  /**
   * Execute git init for local apps via Revolt Desktop IPC (spawns real `git`).
   * Gracefully reports `git not installed` so the caller/user can fix it.
   */
  async gitInitForLocal(
    authorName,
    authorEmail,
    initialMessage,
    stageAll,
    createInitialCommit,
    force
  ) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitInit) {
      return this.returnError(
        {},
        "Local git init requires Revolt Desktop (workspaceLocalAppAPI.gitInit)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const res = await api.gitInit({
      rootPath: root,
      authorName,
      authorEmail,
      initialMessage,
      stageAll: !!stageAll,
      createInitialCommit: !!createInitialCommit,
      defaultBranch: "main",
      force: !!force,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        logger.error(
          { tags: "tools|gitinit|local|git-missing", color1: "red" },
          "Git is not installed on this machine"
        );
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to initialize git repository");
    }

    if (res.alreadyExists) {
      logger.log(
        { tags: "tools|gitinit|local|info", color1: "lime" },
        "Git repository already exists, skipping init (local)"
      );
      return this.returnSuccess(
        {
          initialized: false,
          alreadyExists: true,
          staged: false,
          committed: false,
          branch: res.branch || null,
        },
        "Git repository already exists"
      );
    }

    logger.log(
      { tags: "tools|gitinit|local|info", color1: "lime" },
      "Initialized git repository (local)",
      {
        branch: res.branch || null,
        stagedCount: res.stagedCount || 0,
        committed: !!res.committed,
      }
    );

    return this.returnSuccess(
      {
        initialized: true,
        alreadyExists: false,
        staged: !!res.staged,
        stagedCount: res.stagedCount || 0,
        stagedFiles: [],
        committed: !!res.committed,
        commitHash: res.commitHash || null,
        branch: res.branch || "main",
      },
      "Git repository initialized successfully"
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      // .git doesn't exist
      return false;
    }
  }

  /**
   * Stage all files in the repository
   */
  async stageAllFiles(dir, git, fs) {
    const stagedFiles = [];
    const filesToStage = await this.getAllFiles(dir, fs);

    for (const filepath of filesToStage) {
      // Skip .git directory contents
      if (filepath.startsWith(".git/") || filepath === ".git") {
        continue;
      }

      try {
        await git.add({
          fs: this.fs,
          dir,
          filepath,
        });
        stagedFiles.push(filepath);
      } catch (error) {
        logger.warn(
          {
            tags: "tools|gitinit|stage",
            color1: "yellow",
          },
          `Failed to stage file: ${filepath}`,
          error
        );
      }
    }

    return stagedFiles;
  }

  /**
   * Recursively get all files in the filesystem
   */
  async getAllFiles(dir, fs, basePath = "") {
    const files = [];

    try {
      const entries = await fs.readdir(dir);

      for (const entry of entries) {
        // Skip .git directory
        if (entry === ".git") {
          continue;
        }

        const fullPath = dir === "/" ? `/${entry}` : `${dir}/${entry}`;
        const relativePath = basePath ? `${basePath}/${entry}` : entry;

        try {
          const stats = await fs.stat(fullPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            // Recursively get files from subdirectory
            const subFiles = await this.getAllFiles(fullPath, fs, relativePath);
            files.push(...subFiles);
          } else {
            files.push(relativePath);
          }
        } catch (error) {
          logger.warn(
            {
              tags: "tools|gitinit|scan",
              color1: "yellow",
            },
            `Failed to stat entry: ${fullPath}`,
            error
          );
        }
      }
    } catch (error) {
      logger.warn(
        {
          tags: "tools|gitinit|scan",
          color1: "yellow",
        },
        `Failed to read directory: ${dir}`,
        error
      );
    }

    return files;
  }
}
